/**
 * Service API sécurisé pour les endpoints administratifs
 * Gère automatiquement l'authentification et les erreurs HTTP
 */

import { createAuthenticatedFetch } from '@/api/types/core/common';
import type { Entity } from '@/api/types/core/common';

export interface SecureApiError extends Error {
  status: number;
  code: string;
}

/**
 * Crée une erreur API sécurisée avec des messages localisés
 */
const createSecureApiError = (status: number, message: string): SecureApiError => {
  let localizedMessage = message;
  let code = 'UNKNOWN_ERROR';

  switch (status) {
    case 401:
      localizedMessage = 'Session expirée : Veuillez vous reconnecter';
      code = 'UNAUTHORIZED';
      break;
    case 403:
      localizedMessage = 'Accès refusé : Seuls les administrateurs peuvent effectuer cette action';
      code = 'FORBIDDEN';
      break;
    case 404:
      localizedMessage = 'Ressource non trouvée';
      code = 'NOT_FOUND';
      break;
    case 422:
      localizedMessage = 'Données invalides : Vérifiez les champs requis';
      code = 'VALIDATION_ERROR';
      break;
    case 500:
      localizedMessage = 'Erreur serveur : Veuillez réessayer plus tard';
      code = 'SERVER_ERROR';
      break;
    default:
      localizedMessage = `Erreur ${status}: ${message}`;
      code = 'HTTP_ERROR';
  }

  const error = new Error(localizedMessage) as SecureApiError;
  error.status = status;
  error.code = code;
  return error;
};

/**
 * Service API générique pour les entités avec sécurité intégrée
 */
export const createSecureApiService = <T extends Entity>(baseUrl: string) => {
  const api = createAuthenticatedFetch();

  return {
    /**
     * Récupère toutes les entités
     */
    async getAll(): Promise<T[]> {
      try {
        const response = await api.get<T[]>(`${baseUrl}/all`);
        return response;
      } catch (error: unknown) {
        if (error instanceof Error && error.message.includes('Erreur')) {
          const statusMatch = error.message.match(/Erreur (\d+):/);
          const status = statusMatch ? parseInt(statusMatch[1]) : 500;
          throw createSecureApiError(status, error.message);
        }
        throw createSecureApiError(500, 'Erreur lors de la récupération des données');
      }
    },

    /**
     * Récupère une entité par UUID
     */
    async getById(uuid: string): Promise<T> {
      try {
        const response = await api.get<T>(`${baseUrl}/by_id/${uuid}`);
        return response;
      } catch (error: unknown) {
        if (error instanceof Error && error.message.includes('Erreur')) {
          const statusMatch = error.message.match(/Erreur (\d+):/);
          const status = statusMatch ? parseInt(statusMatch[1]) : 500;
          throw createSecureApiError(status, error.message);
        }
        throw createSecureApiError(404, 'Élément non trouvé');
      }
    },

    /**
     * Crée une nouvelle entité (admin uniquement)
     */
    async create(data: Partial<T>): Promise<T> {
      try {
        const response = await api.post<T>(`${baseUrl}/`, data);
        return response;
      } catch (error: unknown) {
        if (error instanceof Error && error.message.includes('Erreur')) {
          const statusMatch = error.message.match(/Erreur (\d+):/);
          const status = statusMatch ? parseInt(statusMatch[1]) : 500;
          throw createSecureApiError(status, error.message);
        }
        throw createSecureApiError(500, 'Erreur lors de la création');
      }
    },

    /**
     * Met à jour une entité (admin uniquement)
     */
    async update(uuid: string, data: Partial<T>): Promise<T> {
      try {
        const response = await api.put<T>(`${baseUrl}/by_id/${uuid}`, data);
        return response;
      } catch (error: unknown) {
        if (error instanceof Error && error.message.includes('Erreur')) {
          const statusMatch = error.message.match(/Erreur (\d+):/);
          const status = statusMatch ? parseInt(statusMatch[1]) : 500;
          throw createSecureApiError(status, error.message);
        }
        throw createSecureApiError(500, 'Erreur lors de la modification');
      }
    },

    /**
     * Supprime une entité (admin uniquement)
     */
    async delete(uuid: string): Promise<void> {
      try {
        await api.delete(`${baseUrl}/by_id/${uuid}`);
      } catch (error: unknown) {
        if (error instanceof Error && error.message.includes('Erreur')) {
          const statusMatch = error.message.match(/Erreur (\d+):/);
          const status = statusMatch ? parseInt(statusMatch[1]) : 500;
          throw createSecureApiError(status, error.message);
        }
        throw createSecureApiError(500, 'Erreur lors de la suppression');
      }
    }
  };
};

/**
 * Services API sécurisés pour chaque type d'entité
 */
export const secureApiServices = {
  agents: createSecureApiService('/agents'),
  digitalEntities: createSecureApiService('/digital-entities'),
  materialEntities: createSecureApiService('/material-entities'),
  locations: createSecureApiService('/locations'),
  projects: createSecureApiService('/projects'),
  memos: createSecureApiService('/memos'),
  users: createSecureApiService('/users')
} as const;

/**
 * Type helper pour les services API
 */
export type SecureApiService<T extends Entity> = ReturnType<typeof createSecureApiService<T>>; 